/**
 * CABFileUpload — Upload area component for CAB files.
 *
 * Two variants:
 * - Large: Full-size drag-and-drop area (when no files uploaded)
 * - Compact: Slim inline upload bar (when files exist)
 */

import React, { useCallback, useRef } from 'react';
import { Text, Icon, Button } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

interface CABFileUploadProps {
  variant: 'large' | 'compact';
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  isUploading?: boolean;
}

export const CABFileUpload: React.FC<CABFileUploadProps> = ({
  variant,
  onFilesSelected,
  disabled = false,
  isUploading = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Snapshot into a plain array BEFORE resetting the input.
      // FileList is a live reference — clearing the input empties it.
      const files = Array.from(e.target.files);
      // Reset input so same file can be selected again
      e.target.value = '';
      onFilesSelected(files);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setIsDragOver(true);
    }
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (!disabled && !isUploading && e.dataTransfer.files.length > 0) {
      onFilesSelected(Array.from(e.dataTransfer.files));
    }
  }, [disabled, isUploading, onFilesSelected]);

  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      className="hidden"
      onChange={handleFileChange}
      accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.csv,.md,.txt,.html,.zip,.tar,.gz,.png,.jpg,.jpeg,.gif,.py,.js,.ts,.jsx,.tsx,.java,.cs,.cpp,.c,.go,.rs,.rb,.php,.swift,.kt,.json,.xml,.yaml,.yml,.sql,.css,.scss,.vue,.svelte"
    />
  );

  if (variant === 'large') {
    return (
      <div className="mb-bg-v2-lg transition-all duration-300 ease-in-out">
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed rounded-bg-v2-lg
            py-bg-v2-3xl px-bg-v2-xl
            flex flex-col items-center justify-center text-center
            gap-bg-v2-lg cursor-pointer transition-all duration-300 ease-in-out
            w-full
            ${isDragOver
              ? 'border-border-brand bg-bg-tertiary'
              : 'border-border-primary bg-bg-secondary hover:border-border-brand hover:bg-bg-tertiary'}
            ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <div className="text-center w-full">
            <Text variant="text-md" weight="semibold" className="text-text-primary mb-bg-v2-xxs text-center block">
              Upload Documents or Source Code
            </Text>
            <Text variant="text-sm" className="text-text-secondary mb-bg-v2-xs text-center block">
              Drag and drop files here, or click to browse
            </Text>
            <Text variant="text-xs" className="text-text-tertiary text-center block">
              Supports PDF, Documents, images, and source code files (as a zip file)
            </Text>
          </div>
          <div onClick={(e) => e.stopPropagation()}>
            <Button
              size="medium"
              label={
                <div className="flex items-center gap-bg-v2-xs">
                  <Icon name={IconName.Upload} size="md" />
                  <Text variant="text-sm" weight="semibold" color="tertiary">Upload file</Text>
                </div>
              }
              variant="outline"
              onClick={handleClick}
              disabled={disabled || isUploading}
              className="bg-bg-primary border-border-primary shadow-bg-v2-xs"
            />
          </div>
        </div>
        {hiddenInput}
      </div>
    );
  }

  // Compact variant
  return (
    <div className="mb-bg-v2-lg transition-all duration-300 ease-in-out">
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-bg-v2-lg
          p-bg-v2-lg
          flex items-center gap-bg-v2-lg cursor-pointer transition-all duration-300 ease-in-out
          ${isDragOver
            ? 'border-border-brand bg-bg-tertiary'
            : 'border-border-primary bg-bg-secondary hover:border-border-brand hover:bg-bg-tertiary'}
          ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <div className="text-text-tertiary shrink-0">
          <Icon name={IconName.Upload} size="lg" inheritColor />
        </div>
        <div className="flex-1">
          <Text variant="text-sm" weight="semibold" className="text-text-primary mb-bg-v2-xxs">
            Upload Documents or Source Code
          </Text>
          <Text variant="text-xs" className="text-text-secondary">
            Drag and drop or click to browse
          </Text>
        </div>
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <Button
            size="medium"
            label={
              <div className="flex items-center gap-bg-v2-xs">
                <Icon name={IconName.Upload} size="md" />
                <Text variant="text-sm" weight="semibold" color="tertiary">Upload file</Text>
              </div>
            }
            variant="outline"
            onClick={handleClick}
            disabled={disabled || isUploading}
            className="bg-bg-primary border-border-primary shadow-bg-v2-xs"
          />
        </div>
      </div>

      {hiddenInput}
    </div>
  );
};
